import { WaterlilyClient, createQueryKeys } from '@client/api/waterlily';
import type { API } from '@client/api/types';

export const inviteKeys = createQueryKeys('invite');

type InviteResponse = {
  success: boolean;
  result?: unknown;
  error?: string;
  intakeFormLink?: string;
  clientId?: string;
  hasPrefillPlan?: boolean;
};

const sendInvite = (data: IntakeFormEmailProps): Promise<InviteResponse> =>
  WaterlilyClient.post<InviteResponse>('/intake-form-email', data);

const SMS_MFA_BATCH_CONCURRENCY = 4;

async function sendSmsMfaBatchInvite(
  intakeFormEmailProps: IntakeFormEmailProps[],
): Promise<BatchEmailResult> {
  const result: BatchEmailResult = { successCount: 0, errorCount: 0, errors: [] };
  let nextIndex = 0;

  // Each invite gets its own API Gateway invocation so a 35-row batch does not multiply Clerk,
  // form-provider, email, and database latency inside one 29-second request. Keep a small worker
  // pool to bound provider amplification while preserving the existing partial-success behavior.
  const worker = async () => {
    while (nextIndex < intakeFormEmailProps.length) {
      const invite = intakeFormEmailProps[nextIndex++];
      if (!invite) return;

      try {
        const response = await sendInvite({ createNewClient: true, ...invite });
        if (!response.success) {
          throw new Error(response.error || 'Unknown error');
        }
        result.successCount++;
      } catch (error) {
        result.errorCount++;
        result.errors.push({
          email: invite.email,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }
  };

  await Promise.all(
    Array.from(
      { length: Math.min(SMS_MFA_BATCH_CONCURRENCY, intakeFormEmailProps.length) },
      worker,
    ),
  );
  return result;
}

export const inviteApi = {
  sendInvite,
  sendBatchInvite: (data: BatchIntakeFormEmailProps): Promise<BatchEmailResult> =>
    data.intakeFormEmailProps.some(invite => invite.consumerSmsMfa)
      ? sendSmsMfaBatchInvite(data.intakeFormEmailProps)
      : WaterlilyClient.post<BatchEmailResult>('/batch-intake-form-email', data),
};

export interface QuickClientWithFormRequest {
  advisorId: string;
  firstName: string;
  lastName: string;
  email?: string;
  clientRelationshipStage?: ClientRelationshipStage;
  acknowledgedSelfPlanConsent?: boolean;
}

export interface QuickClientWithFormResponse {
  clientId: string;
  formReferenceId: string;
}

export const quickClientKeys = createQueryKeys('quickClient');

export const quickClientApi = {
  createQuickClientWithForm: (data: QuickClientWithFormRequest) =>
    WaterlilyClient.post<QuickClientWithFormResponse>(
      `/advisor/${data.advisorId}/quick-client-with-form`,
      data,
    ),
};

export const agentEnteredIntakeKeys = createQueryKeys('agentEnteredIntake');

export interface CreateAgentEnteredIntakeRequest {
  advisorId: string;
  firstName: string;
  lastName: string;
  email: string;
  /** `YYYY-MM-DD`. Compared as a calendar date on the server, never as an instant. */
  birthDate: string;
  intakeMode: 'intake-form' | 'short-form';
  consumerSmsMfa?: {
    phoneNumber: string;
  };
}

export interface CreateAgentEnteredIntakeForClientRequest {
  clientId: string;
  email: string;
  birthDate: string;
}

/**
 * Opening and tracking a case where the agent enters the customer's information for them.
 *
 * `create` deliberately returns no form link and no `formReferenceId`: there is nothing here an
 * advisor could navigate to before the customer has given permission.
 */
export const agentEnteredIntakeApi = {
  create: ({ advisorId, ...data }: CreateAgentEnteredIntakeRequest) =>
    WaterlilyClient.post<API.AgentEnteredIntakeCreated>(
      `/advisor/${advisorId}/agent-entered-intake`,
      data,
    ),
  createForClient: ({ clientId, ...data }: CreateAgentEnteredIntakeForClientRequest) =>
    WaterlilyClient.post<API.AgentEnteredIntakeCreated>(
      `/client/${clientId}/agent-entered-intake`,
      data,
    ),
  resend: (requestId: string) =>
    WaterlilyClient.post<API.AgentEnteredIntakeCreated & { sentCount: number }>(
      `/agent-entered-intake/${requestId}/resend`,
      {},
    ),
  status: (requestId: string) =>
    WaterlilyClient.get<API.AgentEnteredIntakeRequest>(`/agent-entered-intake/${requestId}`),
  list: (advisorId: string) =>
    WaterlilyClient.get<API.AgentEnteredIntakeRequest[]>(
      `/advisor/${advisorId}/agent-entered-intakes`,
    ),
  cancel: (requestId: string) =>
    WaterlilyClient.delete<{ agentEnteredIntakeId: string; status: string }>(
      `/agent-entered-intake/${requestId}`,
    ),
};
